import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter } from "react-router-dom";

import LoginPage from "@/app/auth/LoginPage";
import ForgotPasswordPage from "@/app/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/app/auth/ResetPasswordPage";
import { RequireAuth } from "@/lib/auth";
import { RouteFallback } from "@/components/app/loading-state";
import { RouteError } from "@/components/app/error-state";

// ---- the three lazy route roots -------------------------------------------
// These are the ONLY React.lazy boundaries in the app. `vite.config.ts` sets no
// manualChunks: Vite 8 / Rolldown rejects the object form, and route-level
// splitting here already produces the chunking we want (contract §7.1).
const StaffRoot = lazy(() => import("@/app/staff/StaffRoot"));
const PortalRoot = lazy(() => import("@/app/portal/PortalRoot"));
const CoachRoot = lazy(() => import("@/app/coach/CoachRoot"));

// ---- the frontend-leads contract (see §7.3) --------------------------------
// Written before the files exist; frontend-leads makes them resolve.
const PublicEnquiryRoutes = lazy(() =>
  import("@/features/leads/public-routes").then((m) => ({ default: m.PublicEnquiryRoutes })),
);

const suspended = (el: ReactNode) => <Suspense fallback={<RouteFallback />}>{el}</Suspense>;

// Every top-level route carries one. react-router's own boundary wins over the
// AppErrorBoundary that sits above RouterProvider in App.tsx, so a route without
// an errorElement renders react-router's raw developer screen — a JS stack trace
// and a "Hey developer" note. On /enquire/* that screen is what a prospective
// customer sees at a gym's front door on a two-year-old handset over 3G, which
// is also the route whose lazy chunk is most likely to fail to load in the
// first place.
export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage />, errorElement: <RouteError /> },
  { path: "/forgot-password", element: <ForgotPasswordPage />, errorElement: <RouteError /> },
  { path: "/reset-password", element: <ResetPasswordPage />, errorElement: <RouteError /> },
  {
    path: "/enquire/*",
    element: suspended(<PublicEnquiryRoutes />),
    errorElement: <RouteError />,
  },
  {
    path: "/portal/*",
    element: <RequireAuth>{suspended(<PortalRoot />)}</RequireAuth>,
    errorElement: <RouteError />,
  },
  {
    path: "/coach/*",
    element: <RequireAuth>{suspended(<CoachRoot />)}</RequireAuth>,
    errorElement: <RouteError />,
  },
  {
    path: "/*",
    element: <RequireAuth>{suspended(<StaffRoot />)}</RequireAuth>,
    errorElement: <RouteError />,
  },
]);
